/**
 * The canvas graph — the picture, as data.
 *
 * Mirrors `omnium_core.scoring.canvas`, and the mirroring is the point: the
 * canvas refuses an illegal line as you draw it, the server refuses the same
 * line as it is stored, and both refuse it for the same stated reason. One of
 * them being wrong is a bug you can see, which is the opposite of what came
 * before — a picture inferred from the engine's fields, disagreeing with them
 * quietly.
 *
 * A **card** is one placement of a node. A node may have several, and they are
 * independent: only the logic is shared, because they are the same node. An
 * **edge** joins two cards. Nothing here is worked out from anything else.
 *
 * The rules, and the only ones:
 *
 *   - a command starts a flow, and nothing connects to a command;
 *   - a command → one rule, or one action — and any number of computes;
 *   - a rule    → one rule, or one action — and any number of computes;
 *   - an action → one action — and any number of computes;
 *   - a compute → one compute, and nothing else.
 *
 * Everything takes at most one line *in*: a flow is a chain, and a card fed by
 * two things is two flows drawn as one. Several computes off one card run in
 * the order they were drawn, so `edges` is an ordered list, not a set.
 */

import type { NodeKind } from "./types";

export interface CanvasCard {
  /** Which drawing of the node this is. */
  id: string;
  /** What it draws. */
  node: string;
  x: number;
  y: number;
}

export interface CanvasEdge {
  fromCard: string;
  toCard: string;
}

export interface CanvasGraph {
  cards: CanvasCard[];
  edges: CanvasEdge[];
  /**
   * How many times this picture has been stored.
   *
   * Sent back with the next save so a picture drawn on top of somebody else's
   * is refused rather than applied. A save carries the whole graph, so without
   * it the second of two writers simply wins and the first author's work is
   * gone with nothing on screen to say so.
   */
  rev?: number;
}

export const EMPTY_GRAPH: CanvasGraph = { cards: [], edges: [], rev: 0 };

/** The one kind a card may have many of — the only kind that fans out. */
export const COMPUTE: NodeKind = "compute";

/** What may follow what. A command is absent as a target everywhere, which is
 *  the first rule; context is absent from both sides, because there is one per
 *  program and it belongs to no flow. */
const FOLLOWS: Record<string, NodeKind[]> = {
  command: ["validation", "trigger", COMPUTE],
  validation: ["validation", "trigger", COMPUTE],
  trigger: ["trigger", COMPUTE],
  compute: [COMPUTE],
};

export function mayFollow(source: NodeKind, target: NodeKind): boolean {
  return (FOLLOWS[source] ?? []).includes(target);
}

/** Every kind a line from here may legally land on — what the canvas lights up. */
export function targetsFor(source: NodeKind): Set<NodeKind> {
  return new Set(FOLLOWS[source] ?? []);
}

/** Whether this line is one of the many a card may have. Only computes fan
 *  out, and only off something that is not itself a compute. */
const fansOut = (source: NodeKind, target: NodeKind) => target === COMPUTE && source !== COMPUTE;

/** What each kind is called in a sentence — the author's words, not the
 *  document's. Nobody authoring a program thinks of a trigger as a database
 *  trigger, and "compute" names the mechanism where "compute" names the work. */
const SAID: Record<string, string> = {
  command: "command",
  validation: "rule",
  trigger: "action",
  compute: "compute",
  context: "read model",
};

const said = (kind: NodeKind) => SAID[kind] ?? kind;
const an = (kind: NodeKind) => `${"aeiou".includes(said(kind)[0]) ? "an" : "a"} ${said(kind)}`;

/**
 * Why this line cannot be drawn, or null if it can.
 *
 * A sentence rather than a boolean, because every refusal is read by the person
 * who just tried it — and "that did not work" is the least useful thing a
 * canvas can say.
 */
export function refusal(
  source: NodeKind,
  target: NodeKind,
  { taken = [], fed = false }: { taken?: NodeKind[]; fed?: boolean } = {},
): string | null {
  if (target === "command") {
    return "nothing connects to a command — a command is where a flow starts";
  }
  if (!mayFollow(source, target)) {
    return source === COMPUTE
      ? "a compute connects to one compute, and to nothing else"
      : `nothing connects ${an(source)} to ${an(target)}`;
  }
  if (fed) return `that ${said(target)} already has a line into it`;
  if (source === COMPUTE) {
    return taken.length ? "a compute connects to one compute, and it already does" : null;
  }
  if (fansOut(source, target)) return null;
  const already = taken.find((kind) => kind !== COMPUTE);
  return already
    ? `that ${said(source)} already continues into ${an(already)} — a flow is one step at a time`
    : null;
}

/* ------------------------------------------------------------------ reading */

/** What each card reaches, and what reaches it — read once per render rather
 *  than searched per card. */
export interface Wiring {
  /** The kinds each card already connects out to, in the order drawn. */
  reaches: Map<string, NodeKind[]>;
  /** Cards something already feeds. */
  fed: Set<string>;
  /** Cards with a line out. */
  sources: Set<string>;
  /** Where each card's lines lead — for the loop test. */
  onward: Map<string, string[]>;
  /**
   * Cards no command reaches — what blocks a publish.
   *
   * "Connected" means *in a flow*, which is more than having a line on it: a
   * rule chained to an action with no command above them is two cards joined
   * to each other and running nothing.
   */
  loose: Set<string>;
  /** The node each card draws — built once per render, not per card. */
  nodeOf: Map<string, string>;
}

export function wiringOf(graph: CanvasGraph, kindOf: (nodeId: string) => NodeKind | null): Wiring {
  const nodeOf = new Map(graph.cards.map((card) => [card.id, card.node]));
  const reaches = new Map<string, NodeKind[]>();
  const onward = new Map<string, string[]>();
  const fed = new Set<string>();
  const sources = new Set<string>();
  for (const edge of graph.edges) {
    const target = nodeOf.get(edge.toCard);
    const kind = target ? kindOf(target) : null;
    if (kind) reaches.set(edge.fromCard, [...(reaches.get(edge.fromCard) ?? []), kind]);
    onward.set(edge.fromCard, [...(onward.get(edge.fromCard) ?? []), edge.toCard]);
    fed.add(edge.toCard);
    sources.add(edge.fromCard);
  }

  // Everything a command reaches, walked once from each command. A command
  // with nothing after it is not a flow either, so it does not seed the walk.
  const live = new Set<string>();
  for (const card of graph.cards) {
    if (kindOf(card.node) !== "command" || !onward.get(card.id)?.length) continue;
    const stack = [card.id];
    while (stack.length > 0) {
      const at = stack.pop()!;
      if (live.has(at)) continue;
      live.add(at);
      stack.push(...(onward.get(at) ?? []));
    }
  }
  const loose = new Set(graph.cards.filter((c) => !live.has(c.id)).map((c) => c.id));
  return { reaches, fed, sources, onward, loose, nodeOf };
}

/** Following the lines out of one card, do we arrive at another? The question
 *  a loop asks. */
function leadsTo(onward: Map<string, string[]>, start: string, wanted: string): boolean {
  const seen = new Set<string>();
  const stack = [start];
  while (stack.length > 0) {
    const at = stack.pop()!;
    if (at === wanted) return true;
    if (seen.has(at)) continue;
    seen.add(at);
    stack.push(...(onward.get(at) ?? []));
  }
  return false;
}

/**
 * Whether a line from one card to another may be drawn, given what is already
 * drawn. The canvas's own check, and the same one the server makes.
 *
 * Reads only from `wiring`, which is built once per render. This is called
 * once per card while a line is in flight — rebuilding anything over all the
 * cards in here would make a single frame cost O(cards²).
 */
export function refusalFor(
  wiring: Wiring,
  kindOf: (nodeId: string) => NodeKind | null,
  fromCard: string,
  toCard: string,
): string | null {
  const fromNode = wiring.nodeOf.get(fromCard);
  const toNode = wiring.nodeOf.get(toCard);
  if (!fromNode || !toNode) return "that card is not on the canvas";
  if (fromCard === toCard) return "a card cannot connect to itself";
  const sourceKind = kindOf(fromNode);
  const targetKind = kindOf(toNode);
  if (!sourceKind || !targetKind) return "that card draws a node this program does not have";
  const why = refusal(sourceKind, targetKind, {
    taken: wiring.reaches.get(fromCard) ?? [],
    fed: wiring.fed.has(toCard),
  });
  if (why) return why;
  // A ring runs nothing: every card takes one line in, so a loop can never be
  // reached from a command.
  if (leadsTo(wiring.onward, toCard, fromCard)) {
    return "that would close a loop, and a flow runs one way";
  }
  return null;
}

/* ------------------------------------------------------------------ editing */

/** A fresh card id for a node. Suffixed rather than random, so a second card
 *  of one node reads as what it is when you see it in the document. */
export function freshCardId(graph: CanvasGraph, nodeId: string): string {
  const taken = new Set(graph.cards.map((card) => card.id));
  if (!taken.has(nodeId)) return nodeId;
  for (let n = 2; ; n += 1) {
    const id = `${nodeId}~${n}`;
    if (!taken.has(id)) return id;
  }
}

export function addCard(graph: CanvasGraph, nodeId: string, at: { x: number; y: number }) {
  const id = freshCardId(graph, nodeId);
  return {
    graph: { ...graph, cards: [...graph.cards, { id, node: nodeId, ...at }] },
    id,
  };
}

/** Take a card off the canvas, and every line that touched it with it. */
export function removeCard(graph: CanvasGraph, cardId: string): CanvasGraph {
  return {
    ...graph,
    cards: graph.cards.filter((card) => card.id !== cardId),
    edges: graph.edges.filter((e) => e.fromCard !== cardId && e.toCard !== cardId),
  };
}

/** Every card of a node, gone — what the bin on a component means. */
export function removeNode(graph: CanvasGraph, nodeId: string): CanvasGraph {
  const gone = new Set(graph.cards.filter((card) => card.node === nodeId).map((c) => c.id));
  return {
    ...graph,
    cards: graph.cards.filter((card) => !gone.has(card.id)),
    edges: graph.edges.filter((e) => !gone.has(e.fromCard) && !gone.has(e.toCard)),
  };
}

export function moveCard(graph: CanvasGraph, cardId: string, at: { x: number; y: number }) {
  return {
    ...graph,
    cards: graph.cards.map((card) => (card.id === cardId ? { ...card, ...at } : card)),
  };
}

export function connect(graph: CanvasGraph, fromCard: string, toCard: string): CanvasGraph {
  return { ...graph, edges: [...graph.edges, { fromCard, toCard }] };
}

export function disconnect(graph: CanvasGraph, fromCard: string, toCard: string): CanvasGraph {
  return {
    ...graph,
    edges: graph.edges.filter((e) => !(e.fromCard === fromCard && e.toCard === toCard)),
  };
}

/** The graph a document carries, or an empty one. The server backfills a
 *  program written before the picture was data, so this is only ever empty for
 *  a program with nothing in it. */
export function graphOf(document: { canvas?: CanvasGraph | null } | null | undefined): CanvasGraph {
  const canvas = document?.canvas;
  if (!canvas || !Array.isArray(canvas.cards)) return EMPTY_GRAPH;
  return {
    rev: canvas.rev ?? 0,
    cards: canvas.cards.map((card) => ({
      id: card.id,
      node: card.node,
      x: card.x ?? 0,
      y: card.y ?? 0,
    })),
    edges: (canvas.edges ?? []).map((edge) => ({
      fromCard: edge.fromCard,
      toCard: edge.toCard,
    })),
  };
}
